// civil.js — Bratislava civil time ↔ epoch-ms boundary (Layer 1)
// Jediné miesto, kde sa deje konverzia medzi civilným časom a epoch-ms.
// EU DST pravidlo implementované čisto aritmeticky, bez Intl ani tzdata.

const BRATISLAVA_STD_OFFSET = 60; // minút, UTC+1
const BRATISLAVA_DST_OFFSET = 120; // minút, UTC+2

// Parse "YYYY-MM-DD" → { year, month, day }
function parseDate(dateStr) {
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) throw new Error(`Invalid date: ${dateStr}`);
  return {
    year: parseInt(m[1], 10),
    month: parseInt(m[2], 10),
    day: parseInt(m[3], 10)
  };
}

// Parse "HH:MM" → { hour, minute }
function parseTime(timeStr) {
  const m = timeStr.match(/^(\d{2}):(\d{2})$/);
  if (!m) throw new Error(`Invalid time: ${timeStr}`);
  return {
    hour: parseInt(m[1], 10),
    minute: parseInt(m[2], 10)
  };
}

// Format { year, month, day } → "YYYY-MM-DD"
function formatDate({ year, month, day }) {
  const y = year.toString().padStart(4, '0');
  const m = month.toString().padStart(2, '0');
  const d = day.toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Zeller-like: deň v týždni pre civilný dátum (1 = pondelok, 7 = nedeľa)
// Používame Sakamoto algoritmus
function dayOfWeek(year, month, day) {
  // Sakamoto: 1 = pondelok, ..., 7 = nedeľa
  const t = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];
  let y = year;
  if (month < 3) y -= 1;
  const dow = (y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) + t[month - 1] + day) % 7;
  // Sakamoto dáva 0 = nedeľa, 1 = pondelok, ...
  // Chceme 1 = pondelok, 7 = nedeľa
  return dow === 0 ? 7 : dow;
}

// Posledná nedeľa v mesiaci (pre marec a október)
// Vracia deň v mesiaci (1-31)
function lastSundayOfMonth(year, month) {
  // Posledný deň v mesiaci
  const lastDay = daysInMonth(year, month);
  // Deň v týždni posledného dňa
  const dow = dayOfWeek(year, month, lastDay);
  // Nedeľa je 7, posledná nedeľa je posledný deň mínus (dow - 7) mod 7
  // Alebo: ak dow === 7, je to nedeľa, inak posunieme späť
  const daysBack = dow === 7 ? 0 : dow;
  return lastDay - daysBack;
}

// Počet dní v mesiaci (month 1-based)
function daysInMonth(year, month) {
  if (month === 2) {
    return isLeap(year) ? 29 : 28;
  }
  if (month === 4 || month === 6 || month === 9 || month === 11) {
    return 30;
  }
  return 31;
}

// Prestupný rok?
function isLeap(year) {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

// Offset v minútach pre daný civilný dátum a čas
// Pre spracovanie gap/overlap potrebujeme vedieť aj presný čas
function getOffsetMinutes(dateStr, hour = 0, minute = 0) {
  const { year, month, day } = parseDate(dateStr);
  
  // Základný offset podľa obdobia
  let offset = BRATISLAVA_STD_OFFSET; // zima
  
  // Marec: posledná nedeľa, 01:00 UTC = 02:00 miestneho času zimný → 03:00 letný
  if (month === 3) {
    const springChangeDay = lastSundayOfMonth(year, 3);
    if (day > springChangeDay) {
      offset = BRATISLAVA_DST_OFFSET;
    } else if (day === springChangeDay) {
      // V deň zmeny: po 02:00 miestneho času (čo je 01:00 UTC) je letný čas
      // Teda 02:00 a viac miestneho = letný čas (+2)
      // Ale pozor: 02:00 miestneho v deň zmeny ešte neexistuje (skok na 03:00)
      // Podľa špecifikácie: 02:30 → 03:30 miestneho = 01:30 UTC
      // Takže ak hour >= 2, použijeme letný offset
      if (hour >= 2) {
        offset = BRATISLAVA_DST_OFFSET;
      }
    }
    return offset;
  }
  
  // Október: posledná nedeľa, 01:00 UTC = 03:00 miestneho letný → 02:00 miestneho zimný
  if (month === 10) {
    const fallChangeDay = lastSundayOfMonth(year, 10);
    if (day < fallChangeDay) {
      offset = BRATISLAVA_DST_OFFSET;
    } else if (day === fallChangeDay) {
      // V deň zmeny: do 03:00 miestneho (včítane) je letný čas
      // 03:00 miestneho letný = 01:00 UTC, po tom je 02:00 miestneho zimný = 01:00 UTC
      // Takže ak hour < 3, je to letný čas, inak zimný
      // Ale pozor: 02:30 miestneho v deň zmeny môže byť prvý alebo druhý výskyt
      // Podľa špecifikácie berieme PRVÝ výskyt (letný čas, +2)
      // Teda 02:00-02:59 je ešte letný čas, od 03:00 je zimný
      if (hour < 3) {
        offset = BRATISLAVA_DST_OFFSET;
      }
    }
    return offset;
  }
  
  // Ostatné mesiace
  if (month > 3 && month < 10) {
    offset = BRATISLAVA_DST_OFFSET;
  }
  
  return offset;
}

// Pomocná funkcia pre interné použitie (s objektom namiesto stringu)
function addCivilDaysInternal({ year, month, day }, n) {
  // Prevedieme na dní od nejakého epochu, pripočítame, prevedieme späť
  // Použijeme proste algoritmus: pripočítavame dni
  let y = year;
  let m = month;
  let d = day + n;
  
  while (d > daysInMonth(y, m)) {
    d -= daysInMonth(y, m);
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  
  while (d < 1) {
    m--;
    if (m < 1) {
      m = 12;
      y--;
    }
    d += daysInMonth(y, m);
  }
  
  return { year: y, month: m, day: d };
}

// Pomocná funkcia na rozdelenie epoch ms na UTC komponenty
function utcFromEpoch(instant) {
  // Použijeme čistú aritmetiku, nie Date objekt
  // Počet dní od epochy (1970-01-01)
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysSinceEpoch = Math.floor(instant / msPerDay);
  const msInDay = instant - daysSinceEpoch * msPerDay;
  
  // Prevedieme dni na dátum (1970-01-01 + days)
  let year = 1970;
  let month = 1;
  let day = 1 + daysSinceEpoch;
  
  // Nájdeme správny rok
  while (day > daysInYear(year)) {
    day -= daysInYear(year);
    year++;
  }
  while (day < 1) {
    year--;
    day += daysInYear(year);
  }
  
  // Nájdeme správny mesiac
  while (day > daysInMonth(year, month)) {
    day -= daysInMonth(year, month);
    month++;
  }
  
  const hour = Math.floor(msInDay / (60 * 60 * 1000));
  const minute = Math.floor((msInDay % (60 * 60 * 1000)) / (60 * 1000));
  
  return { year, month, day, hour, minute };
}

function daysInYear(year) {
  return isLeap(year) ? 366 : 365;
}

// civilToInstant: "YYYY-MM-DD", "HH:MM" | null → epoch ms
export function civilToInstant(date, time) {
  const { year, month, day } = parseDate(date);
  
  if (time === null) {
    // Celodenná udalosť: "do konca dňa" = začiatok nasledujúceho dňa v miestnej časovej zóne
    // To znamená: posunieme sa na nasledujúci deň, 00:00 miestneho času
    const nextDay = addCivilDaysInternal({ year, month, day }, 1);
    const offset = getOffsetMinutes(formatDate(nextDay), 0, 0);
    // UTC čas = miestny čas - offset
    return Date.UTC(nextDay.year, nextDay.month - 1, nextDay.day, 0, 0) - offset * 60 * 1000;
  }
  
  const { hour, minute } = parseTime(time);
  
  // SPRING-FORWARD GAP: V deň zmeny na letný čas, časy 02:00-02:59 neexistujú
  // Podľa špecifikácie §5.3: posúvame dopredu (forward-shift)
  // 02:30 miestneho → 03:30 miestneho = 01:30 UTC
  const springChangeDay = lastSundayOfMonth(year, 3);
  if (month === 3 && day === springChangeDay && hour === 2) {
    // Posúvame na 03:XX miestneho, čo je letný čas (+2)
    // 03:30 miestneho = 01:30 UTC
    const shiftedHour = 3;
    const offset = BRATISLAVA_DST_OFFSET;
    return Date.UTC(year, month - 1, day, shiftedHour, minute) - offset * 60 * 1000;
  }
  
  const offset = getOffsetMinutes(date, hour, minute);
  
  // UTC čas = miestny čas - offset
  return Date.UTC(year, month - 1, day, hour, minute) - offset * 60 * 1000;
}

// instantToCivil: epoch ms → { date: "YYYY-MM-DD", time: "HH:MM" }
export function instantToCivil(instant) {
  // Najprv zistíme, ktorý offset platí v danom okamihu
  // Skúsime oba offsety a zistíme, ktorý dáva zmysel
  
  // Použijeme čistú aritmetiku na získanie UTC času
  const { year: utcYear, month: utcMonth, day: utcDay, hour: utcHour, minute: utcMinute } = utcFromEpoch(instant);
  
  // Skúsime oba offsety
  const candidates = [];
  
  for (const offset of [BRATISLAVA_STD_OFFSET, BRATISLAVA_DST_OFFSET]) {
    // Miestny čas = UTC + offset
    let localMin = utcHour * 60 + utcMinute + offset;
    let localDay = utcDay;
    let localMonth = utcMonth;
    let localYear = utcYear;
    
    // Normalizácia
    while (localMin >= 24 * 60) {
      localMin -= 24 * 60;
      localDay++;
      const dim = daysInMonth(localYear, localMonth);
      if (localDay > dim) {
        localDay = 1;
        localMonth++;
        if (localMonth > 12) {
          localMonth = 1;
          localYear++;
        }
      }
    }
    
    const localHour = Math.floor(localMin / 60);
    const localMinute = localMin % 60;
    
    // Overíme, či tento offset platí pre tento miestny dátum a čas
    const dateStr = formatDate({ year: localYear, month: localMonth, day: localDay });
    const expectedOffset = getOffsetMinutes(dateStr, localHour, localMinute);
    
    if (expectedOffset === offset) {
      candidates.push({
        year: localYear,
        month: localMonth,
        day: localDay,
        hour: localHour,
        minute: localMinute,
        offset
      });
    }
  }
  
  // Mal by byť práve jeden kandidát, alebo dva v čase prechodu späť (fall-back)
  // V čase prechodu dopredu (gap) nie je žiadny kandidát - vtedy berieme letný čas
  // V čase prechodu späť (overlap) sú dvaja - berieme prvý (letný čas, väčší offset)
  
  if (candidates.length === 0) {
    // Gap - použijeme letný čas (+2)
    const offset = BRATISLAVA_DST_OFFSET;
    let localMin = utcHour * 60 + utcMinute + offset;
    let localDay = utcDay;
    let localMonth = utcMonth;
    let localYear = utcYear;
    
    while (localMin >= 24 * 60) {
      localMin -= 24 * 60;
      localDay++;
      const dim = daysInMonth(localYear, localMonth);
      if (localDay > dim) {
        localDay = 1;
        localMonth++;
        if (localMonth > 12) {
          localMonth = 1;
          localYear++;
        }
      }
    }
    
    const localHour = Math.floor(localMin / 60);
    const localMinute = localMin % 60;
    
    return {
      date: formatDate({ year: localYear, month: localMonth, day: localDay }),
      time: `${localHour.toString().padStart(2, '0')}:${localMinute.toString().padStart(2, '0')}`
    };
  }
  
  if (candidates.length === 1) {
    const c = candidates[0];
    return {
      date: formatDate({ year: c.year, month: c.month, day: c.day }),
      time: `${c.hour.toString().padStart(2, '0')}:${c.minute.toString().padStart(2, '0')}`
    };
  }
  
  // Dvaja kandidáti - fall-back overlap, berieme prvý (letný čas, väčší offset)
  // Letný čas má väčší offset (120 vs 60)
  const c = candidates.sort((a, b) => b.offset - a.offset)[0];
  return {
    date: formatDate({ year: c.year, month: c.month, day: c.day }),
    time: `${c.hour.toString().padStart(2, '0')}:${c.minute.toString().padStart(2, '0')}`
  };
}

// addCivilDays: "YYYY-MM-DD", n → "YYYY-MM-DD"
export function addCivilDays(date, n) {
  const parsed = parseDate(date);
  const result = addCivilDaysInternal(parsed, n);
  return formatDate(result);
}

// addCivilMonths: "YYYY-MM-DD", n, overflow → "YYYY-MM-DD" | null
export function addCivilMonths(date, n, overflow) {
  const { year, month, day } = parseDate(date);
  
  let newMonth = month + n;
  let newYear = year;
  
  while (newMonth > 12) {
    newMonth -= 12;
    newYear++;
  }
  while (newMonth < 1) {
    newMonth += 12;
    newYear--;
  }
  
  const dim = daysInMonth(newYear, newMonth);
  
  if (day > dim) {
    if (overflow === 'skip') {
      return null;
    }
    // clamp
    return formatDate({ year: newYear, month: newMonth, day: dim });
  }
  
  return formatDate({ year: newYear, month: newMonth, day });
}

// compareCivil: porovnanie dvoch dátumov "YYYY-MM-DD"
export function compareCivil(a, b) {
  // Porovnáme lexikograficky, lebo formát je YYYY-MM-DD
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

// isoWeekday: "YYYY-MM-DD" → 1..7 (1 = pondelok, 7 = nedeľa)
export function isoWeekday(date) {
  const { year, month, day } = parseDate(date);
  return dayOfWeek(year, month, day);
}

// lastDayOfMonth: year, month (1-based) → "YYYY-MM-DD"
export function lastDayOfMonth(year, month) {
  const day = daysInMonth(year, month);
  return formatDate({ year, month, day });
}
